// src/components/ReflectionPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import { API_BASE_URL } from "../config/apiBase";
import { normalizeStudentId } from "../utils/studentRoutes";

const API = API_BASE_URL;
const WEEKLY_OPTIONS = {
  1: {
    improved_most: [
      "Specific personal detail",
      "Sensory description",
      "Emotional expression",
      "Internal thought",
      "Perspective and voice",
      "Writing flow",
    ],
    hardest_area: [
      "Making writing specific",
      "Writing continuously without overthinking",
      "Using sensory detail naturally",
      "Showing emotion without over-explaining",
      "Controlling third-person voice",
      "Keeping purpose clear",
    ],
    repeated_feedback: [
      "Use more specific detail",
      "Make emotion more visible",
      "Develop one image more deeply",
      "Keep purpose clear",
      "Add internal thought",
      "Use sensory or physical detail",
    ],
    next_focus_label: "One thing I want to focus on in Week 2",
    habit_label: "One habit I want to keep using",
  },
  2: {
    improved_most: [
      "Creating imaginative settings",
      "Adding world rules or daily-life details",
      "Building character contradiction",
      "Showing hidden emotion",
      "Writing dialogue with subtext",
      "Connecting character, setting, and problem",
    ],
    hardest_area: [
      "Making a world feel lived-in",
      "Showing how setting affects people",
      "Revealing character through behavior",
      "Showing hidden emotion without explaining",
      "Writing natural dialogue",
      "Creating a clear hook",
    ],
    repeated_feedback: [
      "Add clearer world rules",
      "Add daily-life details",
      "Show outward vs inner feeling",
      "Use gesture or behavior to reveal character",
      "Reduce direct emotion explanation",
      "Make dialogue imply more than it says",
      "Sharpen the story problem",
    ],
    next_focus_label: "One craft area I want to strengthen in Week 3",
    habit_label: "One character/world/detail habit I want to keep",
  },
  3: {
    improved_most: [
      "Story structure",
      "Escalation",
      "Conflict and stakes",
      "Internal struggle",
      "Pacing control",
      "Endings with emotional payoff",
    ],
    hardest_area: [
      "Creating a clear turning point",
      "Making events connect through cause and effect",
      "Showing external and internal conflict together",
      "Making the character face a difficult choice",
      "Making fast and slow versions feel different",
      "Writing an ending that feels earned",
    ],
    repeated_feedback: [
      "Add clearer escalation",
      "Add a stronger turning point",
      "Make the conflict more specific",
      "Raise the emotional stakes",
      "Use sentence rhythm to control pace",
      "Slow down important moments",
      "Avoid random or cliche endings",
      "Add consequence or emotional shift",
    ],
    next_focus_label: "One story-control skill I want to carry into revision week",
    habit_label: "One scene or ending I would now improve",
  },
  4: {
    improved_most: [
      "I use more specific details",
      "I use stronger sensory descriptions",
      "I think more about character and emotion",
      "I understand pacing better",
      "I revise more deliberately",
      "I feel more confident writing",
    ],
    hardest_area: [
      "Description",
      "Character",
      "Dialogue",
      "Plot",
      "Pacing",
      "Revision",
      "Voice",
    ],
    repeated_feedback: [
      "Use more specific detail",
      "Make emotion more visible",
      "Develop one image more deeply",
      "Show internal thought",
      "Make conflict or stakes clearer",
      "Control pacing more deliberately",
      "Cut repetition",
      "Revise for clarity and emotional effect",
    ],
    next_focus_label: "Which piece or moment from this course are you most proud of?",
    habit_label: "What kind of writer do you want to become next?",
  },
};

function countWords(input) {
  const text = String(input || "").trim();
  if (!text) return 0;
  return text.split(/\s+/).filter(Boolean).length;
}

/**
 * Reflection / Retry Plan page
 * - Saves a "Retry Plan" that is REQUIRED before Attempt 2
 * - Uses attempt=2 (key fix; previously you were saving under attempt=1)
 * - Returns to LessonPage with ?attempt=2&retry_saved=1 so UI can show "Second attempt"
 */
export default function ReflectionPage() {
  const params = useParams();
  const studentId = normalizeStudentId(params.studentId || params.id || localStorage.getItem("student_id"));
  const weekId = params.weekId;
  const lessonId = params.lessonId;
  const navigate = useNavigate();
  const location = useLocation();

  const weekNumber = parseInt(weekId || "1", 10);
  const lessonNumber = parseInt(lessonId || "1", 10);
  const isWeeklyMode = location.pathname.includes("/week-reflection/") || !lessonId;
  const inPortal = Boolean(params.id) && !params.studentId;
  const basePrefix = !inPortal
    ? `/student/${studentId}`
    : `/student/dashboard/${studentId}/lessons`;

  // ✅ This plan belongs to Attempt 2 (not Attempt 1)
  const ATTEMPT = 2;

  const [issueKey, setIssueKey] = useState("off_topic");
  const [planText, setPlanText] = useState("");
  const [weeklyForm, setWeeklyForm] = useState({
    improved_most: "",
    hardest_area: "",
    repeated_feedback: [],
    next_focus: "",
    habit_to_keep: "",
    free_reflection_optional: "",
  });
  const [weeklyOptions, setWeeklyOptions] = useState(WEEKLY_OPTIONS[weekNumber] || WEEKLY_OPTIONS[1]);
  const [weekContext, setWeekContext] = useState(null);
  const [showWeekDetails, setShowWeekDetails] = useState(false);
  const [weeklyErrors, setWeeklyErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadedExisting, setLoadedExisting] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);
  const [weeklySaved, setWeeklySaved] = useState(false);
  const invalidStudentRoute = !studentId || ["undefined", "null"].includes(String(params.studentId || "").toLowerCase());

  const returnTo = useMemo(() => {
    // Optional: allow /reflection?return=/some/path
    const params = new URLSearchParams(location.search);
    const ret = params.get("return");
    return ret && ret.startsWith("/") ? ret : null;
  }, [location.search]);

  function validateWeeklyLocal() {
    const next = {};
    const isFinalWeekReflection = weekNumber === 4;
    if (!weeklyForm.improved_most) {
      next.improved_most = isFinalWeekReflection
        ? "Choose what changed most."
        : "Choose one improvement.";
    }
    if (!weeklyForm.hardest_area) {
      next.hardest_area = isFinalWeekReflection
        ? "Choose one skill to keep developing."
        : "Choose one hard area.";
    }
    if (!weeklyForm.repeated_feedback.length) {
      next.repeated_feedback = isFinalWeekReflection
        ? "Choose at least one kind of feedback."
        : "Choose at least one pattern.";
    }
    if (!String(weeklyForm.next_focus || "").trim()) next.next_focus = "Write a short response.";
    if (!String(weeklyForm.habit_to_keep || "").trim()) next.habit_to_keep = "Write a short response.";
    return next;
  }

  // Load existing retry plan OR weekly reflection
  useEffect(() => {
    let cancelled = false;

    async function loadExisting() {
      if (invalidStudentRoute) {
        setStatusMsg("Your student session could not be identified. Please sign in again.");
        setInitialLoading(false);
        return;
      }
      setStatusMsg("");
      setInitialLoading(true);
      if (isWeeklyMode) {
        try {
          await fetch(`${API}/api/weekly-reflection/mark-complete`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              student_id: studentId,
              week: weekNumber,
            }),
          });
          const reflectionRes = await fetch(
            `${API}/api/weekly-reflection/${encodeURIComponent(
              studentId
            )}/${encodeURIComponent(String(weekNumber))}`
          );
          const reflectionData = await reflectionRes.json().catch(() => null);

          if (cancelled) return;

          const reflection = reflectionData?.reflection;
          if (reflection && typeof reflection === "object") {
            setWeeklyForm({
              improved_most: reflection.improved_most || "",
              hardest_area: reflection.hardest_area || "",
              repeated_feedback: Array.isArray(reflection.repeated_feedback)
                ? reflection.repeated_feedback
                : [],
              next_focus: reflection.next_focus || "",
              habit_to_keep: reflection.habit_to_keep || "",
              free_reflection_optional: reflection.free_reflection_optional || "",
            });
            setLoadedExisting(true);
            setStatusMsg(
              weekNumber === 4
                ? "Loaded your course reflection draft."
                : "Loaded your weekly reflection draft."
            );
          } else {
            setLoadedExisting(false);
          }
          setWeeklyOptions(reflectionData?.options || WEEKLY_OPTIONS[weekNumber] || WEEKLY_OPTIONS[1]);
          setWeekContext(reflectionData?.week_context || null);

        } catch (e) {
          if (!cancelled) setStatusMsg("Could not load weekly reflection.");
        } finally {
          if (!cancelled) setInitialLoading(false);
        }
        return;
      }

      try {
        const res = await fetch(
          `${API}/api/retry-plan/get?student_id=${encodeURIComponent(
            studentId
          )}&week=${weekNumber}&lesson=${lessonNumber}&attempt=${ATTEMPT}`
        );
        const data = await res.json().catch(() => null);

        if (cancelled) return;

        if (data?.ok && data.exists && data.retry_plan) {
          setIssueKey(data.retry_plan.issue_key || "off_topic");
          setPlanText(data.retry_plan.plan_text || "");
          setLoadedExisting(true);
          setStatusMsg("Loaded your saved retry plan for Attempt 2.");
        } else {
          setLoadedExisting(false);
        }
      } catch (e) {
        if (!cancelled) setStatusMsg("Could not load existing retry plan (server issue).");
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    }

    loadExisting();
    return () => {
      cancelled = true;
    };
  }, [studentId, weekNumber, lessonNumber, isWeeklyMode, invalidStudentRoute]);

  async function continueToCurrentLesson() {
    try {
      const res = await fetch(`${API}/api/dashboard?student_id=${encodeURIComponent(studentId)}`);
      const data = await res.json().catch(() => null);
      const currentWeek = parseInt(String(data?.week ?? "1"), 10) || 1;
      const currentLesson = parseInt(String(data?.lesson ?? "1"), 10) || 1;
      navigate(`${basePrefix}/week/${currentWeek}/lesson/${currentLesson}`);
    } catch {
      navigate(`${basePrefix}/week/1/lesson/1`);
    }
  }

  async function handleSaveWeekly() {
    const localErrors = validateWeeklyLocal();
    setWeeklyErrors(localErrors);
    if (Object.keys(localErrors).length > 0) return;

    setLoading(true);
    setStatusMsg("");
    setWeeklySaved(false);
    try {
      const res = await fetch(`${API}/api/weekly-reflection`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          student_id: studentId,
          week: weekNumber,
          ...weeklyForm,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const detail = data?.detail;
        if (detail && typeof detail === "object" && detail.errors) {
          setWeeklyErrors(detail.errors || {});
          setStatusMsg(detail.message || "Weekly reflection validation failed.");
        } else {
          setStatusMsg(typeof detail === "string" ? detail : "Failed to save weekly reflection.");
        }
        return;
      }
      if (data?.completed !== true || data?.status !== "completed") {
        setStatusMsg("Reflection was saved but is not yet marked complete. Please try again.");
        return;
      }
      setWeeklyErrors({});
      setWeeklySaved(true);
      setStatusMsg(
        weekNumber === 4
          ? "Course reflection submitted."
          : "Weekly reflection submitted. Nice work carrying this week forward."
      );
      if (!data?.next_url || typeof data.next_url !== "string" || data.next_url.includes("undefined")) {
        setStatusMsg("Reflection saved, but the next destination was unavailable. Please try again.");
        return;
      }
      navigate(data.next_url);
    } catch (e) {
      setStatusMsg("Error saving weekly reflection.");
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveRetryPlan() {
    if (!planText.trim()) return;

    setLoading(true);
    setStatusMsg("");

    try {
      const res = await fetch(`${API}/api/retry-plan/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          student_id: studentId,
          week: weekNumber,
          lesson: lessonNumber,
          attempt: ATTEMPT,
          issue_key: issueKey,
          plan_text: planText.trim(),
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) {
        setStatusMsg((data && (data.detail || data.message)) || "Failed to save retry plan");
        return;
      }
      const fallbackLessonUrl = `/student/dashboard/${studentId}/lessons/week/${weekNumber}/lesson/${lessonNumber}?attempt=${ATTEMPT}&retry_saved=1`;
      navigate(returnTo || fallbackLessonUrl);
    } catch (e) {
      setStatusMsg("Error saving retry plan");
    } finally {
      setLoading(false);
    }
  }

  function handleBack() {
    if (isWeeklyMode) {
      continueToCurrentLesson();
      return;
    }
    const fallbackLessonUrl = `/student/dashboard/${studentId}/lessons/week/${weekNumber}/lesson/${lessonNumber}?attempt=${ATTEMPT}`;
    navigate(returnTo || fallbackLessonUrl);
  }

  if (initialLoading) {
    return (
      <div style={{ maxWidth: 720, margin: "2rem auto", padding: "1rem" }}>
        Loading...
      </div>
    );
  }

  if (isWeeklyMode) {
    const isComplete =
      weeklyForm.improved_most &&
      weeklyForm.hardest_area &&
      weeklyForm.repeated_feedback.length > 0 &&
      String(weeklyForm.next_focus || "").trim() &&
      String(weeklyForm.habit_to_keep || "").trim();
    const isFinalWeekReflection = weekNumber === 4;
    const introTitle = isFinalWeekReflection ? "Look back at the course." : "Look back at this week.";
    const introCopy = isFinalWeekReflection
      ? "Choose what changed in your writing, then write a short closing reflection."
      : "Choose what improved, what still needs work, and what you want to practise next.";
    const nextFocusLabel = isFinalWeekReflection
      ? "Which piece or moment from this course are you most proud of?"
      : "One thing I will practise next week";
    const habitLabel = isFinalWeekReflection
      ? "What kind of writer do you want to become next?"
      : "One writing habit I want to keep";
    const nextFocusPlaceholder = isFinalWeekReflection
      ? "Name one lesson, scene, sentence, image, character, or revision you remember."
      : "Example: I will practise using one clear sensory detail instead of many vague descriptions.";
    const habitPlaceholder = isFinalWeekReflection
      ? "Example: I want to write stories with stronger characters and more emotional control."
      : "Example: I will keep using specific places, objects, and small actions.";
    const textInputStyle = {
      width: "100%",
      padding: "10px",
      borderRadius: 10,
      border: "1px solid #d1d5db",
      resize: "vertical",
      color: "#111827",
      background: "#fff",
      minHeight: 78,
    };
    const updateRepeated = (value, checked) => {
      setWeeklyForm((prev) => ({
        ...prev,
        repeated_feedback: checked
          ? Array.from(new Set([...(prev.repeated_feedback || []), value]))
          : (prev.repeated_feedback || []).filter((item) => item !== value),
      }));
    };
    const completedCount = weekContext?.completed_lessons?.length || 0;

    return (
      <div className="weekly-reflection" style={{ maxWidth: 880, margin: "1.25rem auto", padding: "1rem" }}>
        <style>{`
          .weekly-reflection { color: #f9fafb; }
          .wr-header { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; flex-wrap: wrap; }
          .wr-pill { background: #111827; color: #fff; padding: 4px 10px; border-radius: 999px; font-size: 12px; font-weight: 800; letter-spacing: .3px; }
          .wr-pill-light { background: #f3f4f6; color: #111827; padding: 4px 10px; border-radius: 999px; font-size: 12px; font-weight: 700; }
          .wr-snapshot { border: 1px solid #dbe3ef; border-radius: 10px; padding: 10px 12px; background: #f8fafc; margin-bottom: 20px; color: #111827; }
          .wr-snapshot-title { font-weight: 850; font-size: 16px; margin-bottom: 4px; color: #111827; }
          .wr-snapshot-line { color: #374151; font-size: 14px; line-height: 1.35; }
          .wr-details-button { border: 0; background: transparent; color: #1d4ed8; padding: 6px 0 0; cursor: pointer; font-weight: 750; font-size: 13px; }
          .wr-details { margin-top: 8px; color: #374151; font-size: 13px; line-height: 1.45; }
          .wr-intro { margin: 16px 0 20px; }
          .wr-intro h3 { margin: 0 0 4px; font-size: 22px; line-height: 1.15; color: #f9fafb; }
          .wr-intro p { margin: 0; color: #cbd5e1; line-height: 1.45; }
          .wr-card { border: 1px solid rgba(226, 232, 240, .32); border-radius: 12px; padding: 18px; margin-bottom: 18px; background: rgba(15, 23, 42, .22); }
          .wr-block-title { font-size: 16px; font-weight: 850; color: #f3f4f6; margin: 0 0 16px; }
          .wr-question { font-size: 14px; font-weight: 800; color: #e5e7eb; margin: 0 0 10px; }
          .wr-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
          .wr-option { display: flex; align-items: center; gap: 8px; min-height: 40px; border: 1px solid #cbd5e1; border-radius: 8px; padding: 8px 10px; background: #fff; color: #1f2937; cursor: pointer; font-size: 14px; line-height: 1.25; font-weight: 650; }
          .wr-option:hover { border-color: #60a5fa; background: #f8fbff; }
          .wr-option.is-selected { border-color: #2563eb; background: #eff6ff; color: #111827; box-shadow: inset 0 0 0 1px rgba(37, 99, 235, .08); }
          .wr-option input { accent-color: #2563eb; flex: 0 0 auto; }
          .wr-field { margin-top: 18px; }
          .wr-field:first-of-type { margin-top: 0; }
          .wr-muted { margin-top: 6px; color: #cbd5e1; font-size: 12px; }
          .wr-error { margin-top: 6px; color: #fca5a5; font-size: 12px; }
          .wr-actions { display: flex; justify-content: center; gap: 10px; margin-top: 12px; }
          .wr-submit-button { padding: 12px 18px; border-radius: 10px; border: 1px solid #1d4ed8; background: #2563eb; color: #fff; cursor: pointer; font-weight: 900; min-width: 260px; box-shadow: 0 8px 18px rgba(37, 99, 235, .22); }
          .wr-submit-button:disabled { border-color: #64748b; background: #475569; color: #e5e7eb; cursor: not-allowed; box-shadow: none; opacity: .9; }
          @media (max-width: 680px) {
            .wr-grid { grid-template-columns: 1fr; }
            .wr-actions { flex-direction: column; }
          }
        `}</style>
        <div className="wr-header">
          <h2 style={{ margin: 0 }}>{isFinalWeekReflection ? "Course Reflection" : "Weekly Reflection"}</h2>
          <span className="wr-pill">
            {isFinalWeekReflection ? "Final Reflection" : `WEEK ${weekNumber}`}
          </span>
          {weekNumber < 4 ? (
            <span className="wr-pill-light">
              Required before Week {weekNumber + 1}
            </span>
          ) : null}
        </div>

        <div className="wr-snapshot">
          <div className="wr-snapshot-title">
            {weekContext?.title || `Week ${weekNumber}`}
          </div>
          {weekContext?.focus_message ? (
            <div className="wr-snapshot-line">
              <strong>Focus:</strong> {weekContext.focus_message}
            </div>
          ) : null}
          {isFinalWeekReflection ? (
            <>
              <div className="wr-snapshot-line">
                <strong>Course completed:</strong> 16 lessons
              </div>
              <div className="wr-snapshot-line">You have reached the final reflection.</div>
            </>
          ) : (
            <div className="wr-snapshot-line">
              <strong>Completed:</strong> {completedCount} {completedCount === 1 ? "lesson" : "lessons"}
            </div>
          )}
          {(weekContext?.completed_lessons?.length || weekContext?.skills?.length) ? (
            <button
              type="button"
              className="wr-details-button"
              onClick={() => setShowWeekDetails((prev) => !prev)}
            >
              {showWeekDetails ? "Hide week details" : "View week details"}
            </button>
          ) : null}
          {showWeekDetails ? (
            <div className="wr-details">
              {weekContext?.completed_lessons?.length ? (
                <div>
                  <strong>Lessons:</strong>{" "}
                  {weekContext.completed_lessons.map((l) => l.title).join(", ")}
                </div>
              ) : null}
              {weekContext?.skills?.length ? (
                <div>
                  <strong>Skills:</strong> {weekContext.skills.slice(0, 4).join(", ")}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="wr-intro">
          <h3>{introTitle}</h3>
          <p>{introCopy}</p>
        </div>

        {statusMsg ? (
          <div style={{ marginBottom: 12, color: "#d1d5db", fontSize: 13 }}>{statusMsg}</div>
        ) : null}
        {weeklySaved ? (
          <div style={{ marginBottom: 12, color: "#86efac", fontSize: 13, fontWeight: 600 }}>
            Reflection saved successfully.
          </div>
        ) : null}

        <div className="wr-card">
          <div className="wr-block-title">A. Quick choices</div>

          <div className="wr-field">
            <div className="wr-question">
              {isFinalWeekReflection ? "What changed most in your writing?" : "What improved most?"}
            </div>
            <div className="wr-grid">
              {(weeklyOptions.improved_most || []).map((option) => {
                const selected = weeklyForm.improved_most === option;
                return (
                  <label key={option} className={`wr-option${selected ? " is-selected" : ""}`}>
                    <input
                      type="radio"
                      name="improved_most"
                      checked={selected}
                      onChange={() => setWeeklyForm((prev) => ({ ...prev, improved_most: option }))}
                    />
                    {option}
                  </label>
                );
              })}
            </div>
          {weeklyErrors.improved_most ? (
            <div className="wr-error">{weeklyErrors.improved_most}</div>
          ) : null}
          </div>

          <div className="wr-field">
            <div className="wr-question">
              {isFinalWeekReflection ? "Which skill do you want to keep developing?" : "What still felt hardest?"}
            </div>
            <div className="wr-grid">
              {(weeklyOptions.hardest_area || []).map((option) => {
                const selected = weeklyForm.hardest_area === option;
                return (
                  <label key={option} className={`wr-option${selected ? " is-selected" : ""}`}>
                    <input
                      type="radio"
                      name="hardest_area"
                      checked={selected}
                      onChange={() => setWeeklyForm((prev) => ({ ...prev, hardest_area: option }))}
                    />
                    {option}
                  </label>
                );
              })}
            </div>
          {weeklyErrors.hardest_area ? (
            <div className="wr-error">{weeklyErrors.hardest_area}</div>
          ) : null}
          </div>

          <div className="wr-field">
            <div className="wr-question">
              {isFinalWeekReflection
                ? "Which feedback helped you most? (Select all that apply.)"
                : "Which feedback came up more than once? (Select all that apply.)"}
            </div>
            <div className="wr-grid">
              {(weeklyOptions.repeated_feedback || []).map((option) => {
                const selected = (weeklyForm.repeated_feedback || []).includes(option);
                return (
                  <label key={option} className={`wr-option${selected ? " is-selected" : ""}`}>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={(e) => updateRepeated(option, e.target.checked)}
                    />
                    {option}
                  </label>
                );
              })}
            </div>
          {weeklyErrors.repeated_feedback ? (
            <div className="wr-error">{weeklyErrors.repeated_feedback}</div>
          ) : null}
          </div>
        </div>

        <div className="wr-card">
          <div className="wr-block-title">B. Short notes</div>
          <div className="wr-field">
          <div className="wr-question">{nextFocusLabel}</div>
          <textarea
            rows={3}
            value={weeklyForm.next_focus}
            placeholder={nextFocusPlaceholder}
            onChange={(e) => setWeeklyForm((prev) => ({ ...prev, next_focus: e.target.value }))}
            style={textInputStyle}
          />
          <div className="wr-muted">
            {countWords(weeklyForm.next_focus)} words
          </div>
          {weeklyErrors.next_focus ? (
            <div className="wr-error">{weeklyErrors.next_focus}</div>
          ) : null}
          </div>

          <div className="wr-field">
          <div className="wr-question">{habitLabel}</div>
          <textarea
            rows={3}
            value={weeklyForm.habit_to_keep}
            placeholder={habitPlaceholder}
            onChange={(e) => setWeeklyForm((prev) => ({ ...prev, habit_to_keep: e.target.value }))}
            style={textInputStyle}
          />
          <div className="wr-muted">
            {countWords(weeklyForm.habit_to_keep)} words
          </div>
          {weeklyErrors.habit_to_keep ? (
            <div className="wr-error">{weeklyErrors.habit_to_keep}</div>
          ) : null}
          </div>

          <div className="wr-field">
          <div className="wr-question">
            {isFinalWeekReflection ? "Optional final note for your Mentor" : "Optional note for your Mentor"}
          </div>
          <textarea
            rows={3}
            value={weeklyForm.free_reflection_optional}
            placeholder={
              isFinalWeekReflection
                ? "Add anything Shaanzae should remember about your writing journey."
                : "Add anything Shaanzae should remember about how you are learning."
            }
            onChange={(e) =>
              setWeeklyForm((prev) => ({ ...prev, free_reflection_optional: e.target.value }))
            }
            style={textInputStyle}
          />
          <div className="wr-muted">
            {isFinalWeekReflection
              ? "Add anything Shaanzae should remember about your writing journey."
              : "Add anything Shaanzae should remember about how you are learning."}{" "}
            {countWords(weeklyForm.free_reflection_optional)} words
          </div>
          </div>
        </div>

        <div className="wr-actions">
          <button
            className="wr-submit-button"
            onClick={handleSaveWeekly}
            disabled={loading || !isComplete}
          >
            {loading
              ? "Submitting..."
              : isFinalWeekReflection
              ? "Submit Reflection & Finish"
              : "Submit Reflection & Continue"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720, margin: "2rem auto", padding: "1rem" }}>
      {/* Strong “Attempt 2” indicator so the student never misses it */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Retry Plan</h2>
        <span
          style={{
            background: "#111827",
            color: "white",
            padding: "4px 10px",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: 0.3,
          }}
        >
          ATTEMPT 2
        </span>
      </div>

      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: "10px 12px",
          background: "#f9fafb",
          color: "#111827",
          marginBottom: 14,
        }}
      >
        <div style={{ fontWeight: 800, marginBottom: 4 }}>Required before Attempt 2</div>
        <div style={{ color: "#374151" }}>
          Pick one issue and write a short plan. This is not a survey — it’s a fast setup for a
          better second attempt.
        </div>
      </div>

      {statusMsg ? (
        <div style={{ marginBottom: 12, color: "#374151", fontSize: 13 }}>{statusMsg}</div>
      ) : null}

      <div style={{ marginTop: 14 }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>What went wrong?</div>
        <select
          value={issueKey}
          onChange={(e) => setIssueKey(e.target.value)}
          style={{
            width: "100%",
            padding: "10px",
            borderRadius: 10,
            border: "1px solid #d1d5db",
            background: "white",
          }}
        >
          <option value="off_topic">I drifted off-topic</option>
          <option value="unclear">My sentences were unclear</option>
          <option value="too_plain">It’s too plain / not vivid</option>
          <option value="incomplete">I rushed / didn’t finish</option>
        </select>
      </div>

      <div style={{ marginTop: 14 }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>
          What will you change for Attempt 2?
        </div>
        <textarea
          rows={4}
          value={planText}
          onChange={(e) => setPlanText(e.target.value)}
          placeholder="1–2 lines. Example: I will rewrite the opening to match the prompt, add 2 vivid details, and end with a clearer closing."
          style={{
            width: "100%",
            padding: "10px",
            borderRadius: 10,
            border: "1px solid #d1d5db",
            resize: "vertical",
          }}
        />
        <div style={{ marginTop: 6, fontSize: 12, color: "#6b7280" }}>
          Keep it short. The Coach should be able to hold you to it.
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
        <button
          onClick={handleBack}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #bbb",
            background: "white",
            cursor: "pointer",
            flex: 1,
            fontWeight: 700,
          }}
        >
          ← Back
        </button>

        <button
          onClick={handleSaveRetryPlan}
          disabled={loading || !planText.trim()}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "none",
            background: "#6f42c1",
            color: "white",
            cursor: loading || !planText.trim() ? "not-allowed" : "pointer",
            fontWeight: 900,
            flex: 1,
            opacity: loading || !planText.trim() ? 0.75 : 1,
          }}
        >
          {loading ? "Saving…" : loadedExisting ? "Update & Return" : "Save & Return"}
        </button>
      </div>
    </div>
  );
}
